import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { AdminSidebar } from "@/components/admin-sidebar"
import { AppContent } from "@/components/app-content"
import { AppShell } from "@/components/app-shell"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { HeaderNavUser } from "@/components/header-nav-user"
import { RoleBadge } from "@/components/role-badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import type { BreadcrumbItem } from "@/types"

interface AdminShellProps {
  breadcrumbs?: BreadcrumbItem[]
}

/**
 * AdminShell — shell for `admin` role.
 *
 * No brand chrome. The role badge ("OKS Admin") makes the global scope
 * unambiguous to staff toggling between tabs.
 */
export function AdminShell({
  children,
  breadcrumbs = [],
}: PropsWithChildren<AdminShellProps>) {
  const { t } = useTranslation()

  return (
    <AppShell variant="sidebar">
      <AdminSidebar />
      <AppContent variant="sidebar" className="overflow-x-hidden">
        <header
          className="border-sidebar-border/50 flex h-16 shrink-0 items-center gap-3 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-6"
          data-testid="admin-shell-header"
        >
          <SidebarTrigger className="-ml-1" />
          <div className="hidden lg:flex">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="flex-1" />
          <RoleBadge role="admin" />
          <HeaderNavUser />
        </header>
        {/* See BrandShell for rationale — avoid nested <main>. */}
        <div
          tabIndex={-1}
          role="region"
          aria-label={t("shell.content_region_label")}
          className="flex-1"
        >
          {children}
        </div>
      </AppContent>
    </AppShell>
  )
}
